// Concept explanations for explain mode. One source of truth for concept text;
// lens descriptions are NOT duplicated here — they come from the backend's lens
// registry (lensMetadata) so they can never drift from the actual formulas.
//
// Writing rule: 1–3 plain sentences. Explain the concept as it applies to the
// data on screen, not the UI chrome. Link concepts to the activation boundary
// and glass-box principles where they apply.

const explanations = {
  object: {
    title: 'Object',
    text: 'Everything saved here is an object: a claim, a note, a tag, a person, a source. Objects are atomic — the meaning lives in the relations drawn between them.',
  },
  relation: {
    title: 'Relation',
    text: 'A named, directional connection between two objects (supports, contradicts, references, …), with a polarity: +1 asserts the connection, −1 denies it. Anyone can create new relation types — structure is read into the graph later, never enforced when writing.',
  },
  voting: {
    title: 'Voting',
    text: 'Users vote agree (+1) or disagree (−1) on a connection — one vote per person, changeable. The vote judges the CONNECTION, not either claim: agreeing with "A supports B" says the link holds, even if you doubt A. To disagree with a claim itself, connect a contradicting claim and vote that edge. What dimension a vote measures is implied by the relation type: "supports" is an epistemic judgment, "trusts" a social one.',
  },
  activation: {
    title: 'Activation boundary',
    text: 'A connection with no votes is inert: it contributes nothing to any trust score, no matter who or what created it. Voting is what activates it. Since only humans vote, every activated edge has been human-verified — this is how AI-gathered data is kept quarantined until a person endorses it.',
  },
  'combined-score': {
    title: 'Combined trust score',
    text: 'The average of all lens scores for this object, from −1 to +1. The equal weighting is a deliberate placeholder — there is no single trust algorithm here, only compared perspectives. Flip on a lens chip to see each perspective separately.',
  },
  stream: {
    title: 'The home stream',
    text: 'Ranked by what needs judgment: contested objects first (widest disagreement between named lens pairs), then the strongest consensus. Never recency, never engagement — attention goes where resolution is closest, not where the arguing is loudest. Objects with no votes anywhere near them are excluded: no signal is absence, not zero.',
  },
  'trust-band': {
    title: 'Trust band',
    text: 'The band spans the range of the lens scores — its width is how much the perspectives disagree about this object. The marker is your own blend, set by your lens weights: a viewpoint you chose, not a system verdict. A wide band is information: the lenses see different things.',
  },
  tension: {
    title: 'Named tension',
    text: 'Two specific lenses disagree about this object in a way with human meaning — for example the raw crowd endorses it while the trust network rejects it. Tensions are surfaced as sentences instead of being averaged away, because the disagreement is the finding.',
  },
  'no-signal': {
    title: 'No signal',
    text: 'Nothing around this object has been activated by votes, so every lens sees nothing. That is an absence, not a zero — a claim nobody has judged is different from a claim the network is split on.',
  },
  thread: {
    title: 'A thread',
    text: 'An authored chain through the graph: someone chose these objects and this order to make a point. Unlike the Walk — which derives the strongest line of reasoning from votes and your lens weights — a thread is arranged by hand, so the connectors between steps say only what comes next. They assert nothing about truth and move no trust score. Each step still carries its own band, computed exactly as it would be anywhere else: arranging a claim does not change how it is judged.',
  },
  heading: {
    title: 'A heading',
    text: 'A heading carries no trust score because it arranges content rather than asserting anything about it. It is a title, a section break, a place in a sequence — nothing about it can be true or false, so a band here would look exactly like the bands on claims while meaning nothing. Its lack of opposition is not agreement either: nobody argues with a section break. The claims a heading sits over keep exactly the scores they had.',
  },
  'edge-handle': {
    title: 'A connection you can argue about',
    text: 'This object stands for one connection in the graph, so that the connection itself can be disputed, tagged or discussed. It carries no trust score of its own — the statement is the connection it stands for, and that connection is still activated by votes on it directly, never by what points at this handle.',
  },
  'thread-loop': {
    title: 'A thread that loops',
    text: 'This step already appeared earlier in the thread, so the chain circles back to it. The loop is drawn rather than quietly cut, because a circular sequence is a finding about the thread — hiding it would present a shorter, straighter thread than the one that exists.',
  },
  'thread-withheld': {
    title: 'Steps you cannot see',
    text: 'The thread continues into objects that are not shared with you, so it ends early here. You are told the count and nothing else — not the content, not where in the sequence the gap falls. Publishing a thread audits every step first and offers to share them, so a published thread normally has none of these.',
  },
  'edge-sentence': {
    title: 'Connections as sentences',
    text: 'Every connection is a statement someone made — so it reads as one. Agree or disagree is a vote on that statement, and votes are what activate a connection in the trust math. Unvoted connections stay inert.',
  },
  lens: {
    title: 'Trust lens',
    text: 'One independent way of scoring trust over the same graph — by votes, by coherence of evidence, by the author\'s reputation, and so on. Lenses are shown separately so you can see when they disagree; disagreement between lenses is information, not noise.',
  },
  engine: {
    title: 'Trust engine',
    text: 'One independent way of scoring people from the trust graph — who trusts whom, and track records. Engines sit beneath the lenses: a lens reads an engine\'s user scores when it needs a reputation input. Like lenses, engines are shown separately and never averaged together.',
  },
  'context-cards': {
    title: 'Related (contextual cards)',
    text: 'Objects connected to this one, ranked by trust rather than recency or popularity. Each card shows why it was surfaced, who verified the connection, and how every lens scores it.',
  },
  'min-trust': {
    title: 'Trust threshold',
    text: 'Hides cards whose combined score falls below the cutoff. This is the anti-engagement dial: you choose the credibility bar, instead of an algorithm choosing what maximizes your attention.',
  },
  threads: {
    title: 'Threads',
    text: 'Chains of supports/contradicts connections — emergent lines of argument in this domain. A thread is not a comment section: each link in the chain is a votable claim-to-claim relationship.',
  },
  'trust-ranking': {
    title: 'Objects by trust',
    text: 'Every object in this domain ordered by its combined lens score. Contested objects (⚡) have both supporting and contradicting activated evidence — worth a look precisely because the network disagrees.',
  },
  domain: {
    title: 'Domain',
    text: 'Domains are just tags: any tag with tagged members acts as a domain, and subcategory tags roll up into their parents. Nothing is pre-categorized — the taxonomy is whatever the graph grows.',
  },
  contested: {
    title: 'Contested',
    text: 'This object has both supporting and contradicting activated edges, or an edge with split votes. The network genuinely disagrees about it.',
  },
  'graph-legend': {
    title: 'Reading the graph',
    text: 'Node size and color = combined trust score. Solid edges assert a relation, dashed edges deny one (negative polarity). Edges with no votes are inert — drawn, but invisible to every trust computation until someone votes.',
  },
  'graph-filters': {
    title: 'Graph filters',
    text: 'Large domains show only the most relevant objects by default — relevance here is connectedness plus trust-score strength. Every filter is a reading lens: hiding unvoted edges, for instance, shows the graph exactly as the trust math sees it.',
  },
  'organized-layout': {
    title: 'Organized layout',
    text: 'Position carries meaning: height = combined trust score (trusted rises to the top, distrusted sinks), and horizontal bands group connected threads of argument, with unconnected objects parked on the right. Free mode is a physics layout — readable, but position means nothing.',
  },
  'lens-weights': {
    title: 'Lens weights',
    text: 'The combined score is a blend of the lenses, and the blend is yours to control: drag a lens to 0 to silence it, above 1 to let it dominate. Default (all 1×) is a plain average — deliberately a placeholder, because there is no single correct trust algorithm. Rankings, colors, and filters all follow your blend.',
  },
  focus: {
    title: 'Focus',
    text: 'Shows only the selected object and everything within the chosen number of connection hops — the local neighborhood of the argument, with the rest of the domain hidden.',
  },

  // --- Added by the explain pass (2026-07-25). Keys that also exist in the
  // backend CORE registry (selfDescribe.ts) get the explore-in-graph link
  // automatically; the rest are UI mechanics, frontend-only on purpose.
  'model-visibility': {
    title: 'Model visibility',
    text: 'Sealed content is never sent to any model, in or out of the app; model-readable content can reach the AI helpers and your own research clients. Sealed is the default — visibility is only ever raised by an explicit act.',
  },
  'hot-vs-new': {
    title: 'Hot vs new',
    text: 'Hot ranks the reading queue by how much talk an item is attracting; it buys attention in the lobby and nowhere else. Engagement never enters a trust score anywhere in the app.',
  },
  'asserted-at': {
    title: 'Asserted date',
    text: 'When a statement was made in the world, not when it was saved here. Timelines and consistency checks run on asserted dates; import dates are only a fallback.',
  },
  decomposition: {
    title: 'Claim decomposition',
    text: 'AI decomposes a document into atomic claims, each anchored to the span where it is made; claims that cannot be located in the text still exist in the graph and appear in the margin. Every edge born this way is inert until a human votes.',
  },
  spine: {
    title: 'The spine',
    text: 'One path down an argument: at each fork the walk takes the activated connection first, then the premise your lens weights score highest. Change your weights and the path changes — it is your reading, not the system\'s verdict.',
  },
  connector: {
    title: 'Connector',
    text: 'The short verb between two steps of a walk is the connection itself — the same votable edge you would see written out in full anywhere else, compacted because both of its endpoints are already on screen as the cards above and below it. The arrow points at the claim being supported, so it flips when you reverse the reading order. Agreeing or disagreeing here is the same act as agreeing anywhere else.',
  },
  'walk-navigation': {
    title: 'Where you are',
    text: 'The dots track your position down the walk. They follow your scrolling rather than needing a keypress, and clicking one jumps to that step. On a long walk the rail shows both ends plus the steps either side of you, with ⋮ standing in for the run it skipped. The address bar keeps the step you are on, so a link you share opens where you were reading.',
  },
  fold: {
    title: 'Fold',
    text: 'Objections and side-connections are folded at the step of the spine they attach to instead of being listed elsewhere. The count is how many connections touch that claim off the spine; the spread tick spans the folded claims\' blended scores — wide means the objections are themselves in dispute.',
  },
  crux: {
    title: 'Crux',
    text: 'Where a disagreement actually turns: a factual dispute can be settled with evidence, a values dispute cannot, and a logical break means the step itself does not follow. The label is a claim about the disagreement — votable like anything else.',
  },
  'dissonance-chain': {
    title: 'Dissonance chain',
    text: 'Someone trusts an author yet endorsed what that author\'s claim contradicts. The chain spells out that path link by link, so a fork traces to specific people and specific votes rather than to a score.',
  },
  question: {
    title: 'Question',
    text: 'Something to be settled rather than asserted. A question has no truth value, so it carries no trust score — a number here would look exactly like the numbers on claims while meaning nothing. Claims attach to it in two ways: one that merely bears on it, and one that says the claim TURNS ON it, which is a crux named before anyone has answered it. Voting on those connections says whether the link holds, never whether the question is true.',
  },
  qualifies: {
    title: 'Qualification',
    text: 'A bounded exception: the claim it points at still stands, but this one names a limit, a condition, or a group where it does not fully hold. That is what separates it from a contradiction — both statements are true at once. Because it is neither agreement nor opposition, a qualification is counted on its own and never pulls a claim\'s score up or down; a claim that attracts many of them is not contested, it is conditional.',
  },
  refine: {
    title: 'Refining a connection',
    text: 'When a connection joins the right two claims by the wrong name, refining it adds the corrected connection and marks the original as not holding. Nothing is deleted — the original keeps its history, and votes decide which reading survives. Your correction starts unvoted like any other suggestion, so refining costs nothing until someone agrees with it.',
  },
  'restates-cluster': {
    title: 'Restatement cluster',
    text: 'Statements that say the same thing collapse onto one canonical claim, so a vote lands once instead of splitting across rewordings; edges drawn on a restatement follow it to the canonical.',
  },
  snapshot: {
    title: 'Snapshot',
    text: 'A frozen copy of a draft as it read at one moment. The living entry keeps its identity and connections; the copy hangs off it by a revises edge, so nothing linked has to be rewired.',
  },
  'the-table': {
    title: 'The table',
    text: 'Comments are talk, not evidence: their votes only sort the thread and never reach a trust lens. A reply enters the graph only when typed as rebuts, supports, or asks — then it is a votable connection like any other.',
  },
  viewset: {
    title: 'Viewset',
    text: 'A saved filter over the graph: tags and relations whose matches become a feed. The same viewset narrows the needs-judgment stream, so it doubles as a reading scope.',
  },
  'walk-vs-map': {
    title: 'Walk vs map',
    text: 'The Walk is one guided path — the strongest line of reasoning under your lens weights, with objections folded where they attach. The Map is the same argument with nothing chosen for you: every claim, every fork, every link at once.',
  },
  'common-ground': {
    title: 'Common ground',
    text: 'Premises the supporting and opposing sides both have activated votes behind — the part of the argument that is not in dispute.',
  },
  'inference-step': {
    title: 'Inference step',
    text: 'A formal reasoning step someone proposed over these claims. The graph never enforces logic: validity is the community\'s call on the step — an upheld supports edge is a valid step, a rejected one is a fallacy.',
  },
  'claim-state': {
    title: 'Claim states',
    text: 'Computed live from the graph: survives division means it holds up across otherwise-divided groups, contested means supporting and contradicting evidence are both activated, mixed signal means the lenses disagree, and no judgments yet means no edge around it has a vote.',
  },
  'reading-depth': {
    title: 'Reading depth',
    text: 'How much of the graph is brought to the page: 0 is plain text, 1 underlines the claims (click one for its case), 2 opens each judged claim\'s full case beneath its paragraph — lenses and connections as a row of cards, with unjudged suggestions behind a quiet chip. Same document at every depth — only the apparatus changes.',
  },
  'standing-counter': {
    title: 'Standing counter',
    text: 'The contradicting claim with the strongest activated backing right now. It is the graph\'s current best objection, not a settled refutation — it moves as votes move.',
  },
  staking: {
    title: 'Staking',
    text: 'Staking lifts a comment\'s claims into the graph as real claims, keeping the author\'s attribution. It is how table talk becomes something the trust math can see.',
  },
  'reading-queue': {
    title: 'The reading queue',
    text: 'The queue is the front door: imported documents waiting to be read, filled by feed subscriptions and by-hand imports. Judgment starts in the Reader, not here.',
  },
  feeds: {
    title: 'Feeds',
    text: 'A feed is just a link object you are subscribed to. Refreshing imports its items through the same pipeline as pasting a URL by hand — re-running is safe, anything already imported is skipped, and feed items are never auto-decomposed.',
  },
  'url-import': {
    title: 'URL import',
    text: 'A lone URL is fetched, its article text extracted, and the outlet and byline author become source identities in the trust graph — so anything later said about the piece is attributable. If the live page is blocked, an Internet Archive snapshot is used instead.',
  },
  'clip-provenance': {
    title: 'Clip provenance',
    text: 'The page you clipped from is imported too, and the clip is linked to it with a derived_from connection, so the quote carries its source from birth.',
  },
  'arrival-path': {
    title: 'Arrival path',
    text: 'How this document got here: fetched live from its source, recovered from an Internet Archive snapshot, or relayed by a model that was reading it (for pages the importer cannot reach). A relayed capture is weaker provenance — the relay could misquote — so it is marked instead of passing as a fetch.',
  },
  'write-modes': {
    title: 'Tags on this entry',
    text: 'Type a tag and press Enter to attach it; a name that does not exist yet is created. Your own most-used tags are offered first, and the writing modes — journal, creative, study, research, social-draft — are suggestions in the same list, not a fixed set. Every tag is an ordinary inert connection you can vote on, and tagging never changes who can see the entry.',
  },
  'support-degree': {
    title: 'Support in and out',
    text: 'How many activated connections point at this claim versus out from it. In is evidence others have aimed at it; out is what it is being used to argue.',
  },
  'shared-neighborhood': {
    title: 'Shared neighborhood',
    text: 'Objects that connect to two or more of the compared claims — the evidence they have in common, and the place their disagreement usually lives.',
  },
  'consistency-score': {
    title: 'Consistency',
    text: 'The share of this actor\'s claims not caught in a recorded self-contradiction. It measures internal coherence over time, not truth — and only counts contradictions the graph has actually recorded.',
  },
  'diagnostic-engine': {
    title: 'Diagnostic engine',
    text: 'A diagnostic engine flags an anomaly instead of ranking trust, and is never folded into any combined readout. A flag is a suspicion signal, not a verdict.',
  },
  'trust-anchor': {
    title: 'Trust anchor',
    text: 'Trust propagation has to start somewhere. If you have expressed trust, you are the anchor and every number is relative to your network; otherwise everyone anchors equally and the result is a cold-start global view.',
  },
  convergence: {
    title: 'Convergence',
    text: 'Scores are computed by repeating a propagation step until they stop moving. "Stopped at N" means the iteration cap was hit first — treat those numbers as provisional.',
  },
  'teleport-alpha': {
    title: 'Teleport α',
    text: 'How often propagation jumps back to the anchor instead of following another trust edge. Higher keeps scores closer to your own direct judgments; lower lets trust travel further through the network.',
  },
  'trust-cluster': {
    title: 'Trust cluster',
    text: 'Groups the trust graph separates into. Two people in different clusters are largely trusted by different crowds; the clustering is descriptive, not a judgment.',
  },
  'viewset-filters': {
    title: 'Viewset filters',
    text: 'Each filter is a relation plus an optional target: tagged → climate keeps anything carrying that tag, and a blank target matches any object holding the relation at all. Filters union into one feed, and included viewsets contribute their filters too.',
  },
  'viewset-visibility': {
    title: 'Viewset visibility',
    text: 'Public means anyone can open this viewset and see the feed it produces — but the objects inside still obey their own visibility. Private viewsets can be shared with specific people or groups.',
  },
  'mcp-access': {
    title: 'MCP access',
    text: 'Your own LLM client can use Worldview as a research tool: search the graph, read standing arguments, import sources, and scaffold tags and relations. Everything it creates arrives inert — the model can never vote, so nothing it touches enters a trust score until you activate it here.',
  },
  'personal-access-token': {
    title: 'Personal access token',
    text: 'A bearer token that acts as you, limited to model-visible content. Only its hash is stored, so it is shown exactly once — mint one per client and revoke any that leaks.',
  },
};

export default explanations;
